import { useEffect, useRef, useState } from 'react';
import { autocomplete, mapsErrorCopy, newSessionToken, placeDetails } from '../models/mapsApi';

// One Google billing session per search: the token is shared by the
// suggestions and the chosen place's details, then renewed.
export function usePlaceSearch({ onSelect, delay = 250 } = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = useRef(newSessionToken()), sequence = useRef(0), timer = useRef(null);
  const latest = useRef({ text: '', rows: [] });
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  useEffect(() => () => { sequence.current += 1; clearTimeout(timer.current); }, []);

  const fetchRows = async (text, current) => {
    const rows = await autocomplete(text, token.current);
    if (current !== sequence.current) return null;
    latest.current = { text, rows };
    setOptions(rows); setError('');
    return rows;
  };
  const search = text => {
    clearTimeout(timer.current);
    const trimmed = String(text || '').trim(), current = ++sequence.current;
    if (trimmed.length < 2) { latest.current = { text: '', rows: [] }; setOptions([]); setLoading(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { await fetchRows(trimmed, current); }
      catch (failure) { if (current === sequence.current) { setOptions([]); setError(mapsErrorCopy(failure)); } }
      finally { if (current === sequence.current) setLoading(false); }
    }, delay);
  };
  const select = async placeId => {
    clearTimeout(timer.current);
    const current = ++sequence.current;
    setLoading(true);
    try {
      const place = await placeDetails(placeId, token.current);
      token.current = newSessionToken();
      if (current !== sequence.current) return null;
      latest.current = { text: '', rows: [] }; setOptions([]); setError('');
      if (place) onSelectRef.current?.(place);
      return place;
    } catch (failure) {
      if (current === sequence.current) setError(mapsErrorCopy(failure));
      return null;
    } finally { if (current === sequence.current) setLoading(false); }
  };
  // Enter without picking a suggestion takes the first match for the typed text.
  const selectFirst = async text => {
    clearTimeout(timer.current);
    const trimmed = String(text || '').trim();
    if (trimmed.length < 2) return null;
    let rows = latest.current.text === trimmed ? latest.current.rows : null;
    if (!rows) {
      const current = ++sequence.current;
      setLoading(true);
      try { rows = await fetchRows(trimmed, current); }
      catch (failure) { if (current === sequence.current) setError(mapsErrorCopy(failure)); return null; }
      finally { if (current === sequence.current) setLoading(false); }
      if (!rows) return null;
    }
    if (!rows.length) { setError('No matching place found.'); return null; }
    return select(rows[0].place_id);
  };
  const clear = () => { clearTimeout(timer.current); sequence.current += 1; latest.current = { text: '', rows: [] }; setOptions([]); setLoading(false); setError(''); };
  return { options, loading, error, search, select, selectFirst, clear };
}
